import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { CreateBoardDialog } from '@/features/board-manage';
import {
  useDeletedWorkspaces,
  useMyBoards,
  useMyInvitations,
  useMyRequests,
} from '@/entities/collab';
import { effectiveSort, matchesFilters, useCatalogFilters } from '@/entities/catalog';
import { readRecentBoardIds } from '@/entities/board';
import { readRecentDatabaseIds, useMyDatabases } from '@/entities/database';
import { useViewer } from '@/entities/viewer';
import { canCreateTasks, useWorkspaces, type Workspace } from '@/entities/workspace';
import { CatalogToolbar } from '@/features/catalog-filters';
import { HubNav } from '@/features/hub-nav';
import { CREATE_DATABASE, CreateDatabaseDialog } from '@/features/database-catalog';
import { ImportDatabaseDialog } from '@/features/database-portability';
import { QuickCreateDialog } from '@/features/quick-create';
import { HomeCalendarPreview } from '@/features/calendar';
import { taskPath } from '@/entities/my-tasks';
import { originStateFrom } from '@/shared/lib/origin';
import { ErrorState, Icons, IconButton, LoadingState } from '@/shared/ui';

import { BoardCatalog } from './BoardCatalog';
import { CreateMenu } from './CreateMenu';
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog';
import { boardFilterable, databaseFilterable, HOME_OFFERED, HOME_SORTS } from './home-filters';
import { HomeDatabases } from './HomeDatabases';
import { HomeQuickSearch } from './HomeQuickSearch';
import styles from './HomeDashboard.module.css';
import { MyWorkPanel } from './MyWorkPanel';
import { WorkspaceStrip } from './WorkspaceStrip';

/**
 * The signed-in home, under the unchanged top navigation:
 *
 * 1. page actions - the title, one "Create" menu and ONE search over boards and workspaces;
 * 2. workspaces - the personal one first, then the team workspaces, as compact chips;
 * 3. the board catalog (about three quarters of the width) beside the compact "My work" panel
 *    (the rest); on a phone these stack as search -> workspaces -> my work -> boards.
 *
 * The personal workspace is not presented as a team workspace. Board classification (personal /
 * team / favorites) is exact and a board is listed once whatever grants provide it.
 */
export function HomeDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const viewer = useViewer();
  const boards = useMyBoards({ limit: 1000 });
  const workspaces = useWorkspaces();
  const invitations = useMyInvitations();
  const requests = useMyRequests();
  const deleted = useDeletedWorkspaces();
  const databases = useMyDatabases();
  const [query, setQuery] = useState('');
  const { filters, update, reset } = useCatalogFilters();
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [creatingDatabase, setCreatingDatabase] = useState(false);
  const [importingDatabase, setImportingDatabase] = useState(false);
  const [taskIn, setTaskIn] = useState<Workspace | null>(null);

  const personal = workspaces.data?.find((w) => w.workspace_type === 'personal');
  const teams = useMemo(
    () => (workspaces.data ?? []).filter((w) => w.workspace_type === 'team'),
    [workspaces.data],
  );
  const taskTargets = useMemo(() => teams.filter((w) => canCreateTasks(w)), [teams]);
  const databaseTargets = useMemo(
    () => (workspaces.data ?? []).filter((w) => w.permissions.includes(CREATE_DATABASE)),
    [workspaces.data],
  );

  // How many of each type the OTHER filters and the search leave (the badges of the type control).
  const counts = useMemo(() => {
    const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
    const anyType = { ...filters, type: 'all' as const };
    const openedBoards = new Set(userId ? readRecentBoardIds(userId) : []);
    const openedDatabases = new Set(userId ? readRecentDatabaseIds(userId) : []);
    const spaces = new Map((workspaces.data ?? []).map((w) => [w.id, w.workspace_type] as const));
    const names = new Map(
      (workspaces.data ?? []).map(
        (w) =>
          [w.id, w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name] as const,
      ),
    );
    const board = (boards.data ?? []).filter((item) =>
      matchesFilters(boardFilterable(item, openedBoards), anyType, userId, query, HOME_OFFERED),
    ).length;
    const database = (databases.data ?? []).filter((item) =>
      matchesFilters(
        databaseFilterable(item, openedDatabases, spaces, names),
        anyType,
        userId,
        query,
        HOME_OFFERED,
      ),
    ).length;
    return { all: board + database, board, database };
  }, [filters, query, boards.data, databases.data, workspaces.data, viewer, t]);

  if (boards.isPending || workspaces.isPending) return <LoadingState />;
  if (boards.isError)
    return <ErrorState error={boards.error} onRetry={() => void boards.refetch()} />;
  if (workspaces.isError) {
    return <ErrorState error={workspaces.error} onRetry={() => void workspaces.refetch()} />;
  }
  const user = viewer.status === 'authenticated' ? viewer.user : null;
  // Useful by default: what the viewer opened lately, else what changed lately.
  const openedAny = user ? readRecentBoardIds(user.id).length > 0 : false;
  const sort = effectiveSort(filters, HOME_SORTS, openedAny ? 'opened' : 'updated');
  const waiting = (invitations.data?.length ?? 0) + (requests.data?.length ?? 0);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.titles}>
          <h1 className={styles.title}>{t('home.title')}</h1>
          <p className={styles.subtitle}>{t('home.subtitle')}</p>
        </div>
        <div className={styles.actions} role="group" aria-label={t('home.actions')}>
          {databaseTargets.length > 0 && (
            <IconButton
              label={t('home.importDatabase')}
              icon={<Icons.import />}
              onClick={() => {
                setImportingDatabase(true);
              }}
              data-testid="import-database"
            />
          )}
          <CreateMenu
            personal={personal}
            taskTargets={taskTargets}
            onNewTask={setTaskIn}
            onNewBoard={() => {
              setCreatingBoard(true);
            }}
            {...(databaseTargets.length > 0 && {
              onNewDatabase: () => {
                setCreatingDatabase(true);
              },
            })}
            onNewWorkspace={() => {
              setCreatingWorkspace(true);
            }}
          />
        </div>
      </header>

      <HubNav />

      <CatalogToolbar
        query={query}
        onQuery={setQuery}
        searchLabel={t('home.searchAll')}
        filters={filters}
        onChange={update}
        onReset={reset}
        offered={HOME_OFFERED}
        sorts={HOME_SORTS}
        sort={sort}
        counts={counts}
      />

      <HomeQuickSearch
        query={query}
        workspaces={workspaces.data}
        boards={boards.data}
        databases={databases.data ?? []}
      />

      {waiting > 0 && (
        <Link to="/app/invitations" className={styles.notice} data-testid="invitations-link">
          <Icons.mail />
          <span>{t('home.invitationsWaiting', { count: waiting })}</span>
          <Icons.arrowRight />
        </Link>
      )}

      <div className={styles.main}>
        <div className={styles.mainSide}>
          <MyWorkPanel />
        </div>
        <div className={styles.mainCatalog} data-testid="home-catalog">
          <WorkspaceStrip
            personal={personal}
            teams={teams}
            query={query}
            filters={filters}
            userId={user?.id ?? null}
            trashCount={(deleted.data ?? []).length}
          />
          {filters.type !== 'database' && (
            <BoardCatalog
              boards={boards.data}
              query={query}
              filters={filters}
              sort={sort}
              userId={user?.id ?? null}
              locale={i18n.language}
            />
          )}
          <HomeDatabases
            workspaces={workspaces.data}
            query={query}
            filters={filters}
            sort={sort}
            userId={user?.id ?? null}
            locale={i18n.language}
          />
        </div>
        <div className={styles.mainCalendar}>
          <HomeCalendarPreview
            boards={boards.data}
            timeZone={user?.timezone ?? null}
            locale={i18n.language}
            calendarPath="/app/calendar"
            onOpenCard={(cardId, boardId) => void navigate(taskPath(boardId, cardId))}
          />
        </div>
      </div>

      {creatingDatabase && (
        <CreateDatabaseDialog
          workspaces={databaseTargets}
          onCreated={(database) => {
            setCreatingDatabase(false);
            void navigate(`/app/databases/${database.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setCreatingDatabase(false);
          }}
        />
      )}
      {importingDatabase && (
        <ImportDatabaseDialog
          workspaces={databaseTargets}
          onImported={(database) => {
            setImportingDatabase(false);
            void navigate(`/app/databases/${database.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setImportingDatabase(false);
          }}
        />
      )}
      {creatingWorkspace && (
        <CreateWorkspaceDialog
          onCreated={(workspace) => {
            setCreatingWorkspace(false);
            void navigate(`/app/workspaces/${workspace.id}`);
          }}
          onClose={() => {
            setCreatingWorkspace(false);
          }}
        />
      )}
      {creatingBoard && personal && (
        <CreateBoardDialog
          workspaceId={personal.id}
          onCreated={(created) => {
            setCreatingBoard(false);
            void navigate(`/app/boards/${created.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setCreatingBoard(false);
          }}
        />
      )}
      {taskIn && (
        <QuickCreateDialog
          workspaceId={taskIn.id}
          timezone={user?.timezone ?? null}
          onClose={() => {
            setTaskIn(null);
          }}
        />
      )}
    </div>
  );
}
